import type { SalesTotal } from '../../data/sales-total';
import type { InventoryValue } from '../../data/store-stats';
import {
  formatPercent,
  preciseCurrencyFormatter,
  shortCurrencyFormatter,
} from '../../helpers/format-currency';
import { monthOf, yearOf } from '../../helpers/year-month';
import type { DisplayType } from './types';

// Gross margin as a fraction of sales. Returns null for months with no sales,
// so the line breaks rather than crashing to 0% on empty/future months.
export const getMarginPercent = (total: SalesTotal): number | null => {
  if (total.sales === 0) {
    return null;
  }

  return (total.sales - total.costs) / total.sales;
};

export const getDisplayValue = (total: SalesTotal, displayType: DisplayType): number => {
  switch (displayType) {
    case 'Sales': {
      return total.sales;
    }

    case 'Transactions': {
      return total.transactions;
    }

    case 'Gross Margin': {
      return total.sales - total.costs;
    }

    case 'Average Order Value': {
      if (total.transactions === 0) {
        return 0;
      }

      return total.sales / total.transactions;
    }
  }
};

export const formatDisplayValue = (
  value: number | null | undefined,
  displayType: DisplayType,
  asPercent = false
): string => {
  if (asPercent) {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return 'N/A';
    }

    return formatPercent(value);
  }

  if (!value || Number.isNaN(value)) {
    return 'N/A';
  }

  if (displayType === 'Transactions') {
    return value.toLocaleString('en-US');
  }

  if (displayType === 'Average Order Value') {
    return preciseCurrencyFormatter(value);
  }

  return shortCurrencyFormatter(value);
};

export type InventoryValueBasis = 'cost' | 'retail';

// What a chart plots. The toggle offers the two money bases; the unit count
// rides along in the same snapshot, so a units chart groups the same way.
export type InventoryValueMetric = InventoryValueBasis | 'qty_on_hand';

type InventoryValueYear = { year: number; monthly: (number | null)[] };

// groupValueHistoryByYear lays month-end snapshots out on a Jan–Dec grid for
// the chosen metric, newest year first: the latest snapshot's year, then up to
// `priorYears` earlier years that have any data. Months with no snapshot are
// null so the chart draws a gap instead of a drop to $0.
export const groupValueHistoryByYear = (
  history: InventoryValue[],
  metric: InventoryValueMetric,
  priorYears = 4
): InventoryValueYear[] => {
  const latest = history.at(-1);
  if (!latest) {
    return [];
  }

  const latestYear = yearOf(latest.month);

  return Array.from({ length: priorYears + 1 }, (_, i) => latestYear - i)
    .map((year) => {
      const monthly: (number | null)[] = Array.from({ length: 12 }, () => null);
      for (const snapshot of history) {
        if (yearOf(snapshot.month) === year) {
          monthly[monthOf(snapshot.month) - 1] = snapshot[metric];
        }
      }

      return { year, monthly };
    })
    .filter(({ monthly }, i) => i === 0 || monthly.some((value) => value !== null));
};

// yoyChange compares one period against the same period a year earlier, as a
// fraction. A missing side, or a previous value of zero, has no answer — the
// caller shows a dash rather than an infinity.
export const yoyChange = (value?: number, previous?: number): number | undefined =>
  value !== undefined && previous !== undefined && previous !== 0
    ? (value - previous) / previous
    : undefined;

// yoyPoints is the same comparison for a figure that is already a percentage.
// A relative change of a margin misleads — 20% to 22% is two points, not ten
// percent — so the Gross Margin chart's % mode reports the difference instead.
export const yoyPoints = (value?: number, previous?: number): number | undefined =>
  value !== undefined && previous !== undefined ? value - previous : undefined;

// Every series on the year-over-year charts belongs to exactly one year, and
// these two are the only places that format is written down. A series says
// which year it is through its own id, so the tooltip can group rows by year
// without each widget having to state it a second time.
export const yearSeriesId = (year: number, suffix = ''): string => `year${year}${suffix}`;

export const yearOfSeriesId = (seriesId: string | number): number | undefined => {
  const match = /^year(\d{4})/.exec(String(seriesId));

  return match ? Number(match[1]) : undefined;
};
